/**
 * User Detail/Edit Page
 *
 * Detailed user profile view and edit interface for admins to manage
 * individual user accounts, permissions, and view activity logs.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Edit, Save, Key, Trash2 } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/forms/Input';
import { formatDate } from '../../utils/formatting';

export const UserDetailPage: React.FC = () => {
  const { t } = useTranslation(['common', 'admin']);
  const { id } = useParams<{ id: string }>();
  const [editMode, setEditMode] = useState(false);

  // Mock user data
  const [userData, setUserData] = useState({
    id: id || '1',
    name: 'Kantin Warisan Sdn Bhd',
    email: 'admin@kantinwarisan.com',
    phone: '+60 12-345 6789',
    organization: 'Kantin Warisan Sdn Bhd',
    role: 'COMPANY',
    status: 'ACTIVE',
    registrationDate: '2024-01-15',
    lastLogin: '2025-01-01',
  });

  // Mock activity log
  const activityLog = [
    { timestamp: '2025-01-01 14:30', action: 'Logged in', details: 'Successful login from 192.168.1.1' },
    { timestamp: '2025-01-01 14:35', action: 'Updated application', details: 'Modified application APP-001' },
    { timestamp: '2024-12-31 10:15', action: 'Uploaded document', details: 'Uploaded halal certificate' },
    { timestamp: '2024-12-30 16:20', action: 'Logged out', details: 'Session ended' },
    { timestamp: '2024-12-30 09:00', action: 'Logged in', details: 'Successful login from 192.168.1.5' },
  ];

  // Mock associated records
  const associatedRecords = {
    applications: 3,
    documents: 12,
    payments: 5,
  };

  const handleSave = () => {
    alert(t('admin:userDetail.changesSaved'));
    setEditMode(false);
  };

  const handleResetPassword = () => {
    if (window.confirm(t('admin:userDetail.confirmPasswordReset'))) {
      alert(t('admin:userDetail.passwordResetSent'));
    }
  };

  const handleDeleteAccount = () => {
    if (window.confirm(t('admin:userDetail.confirmDeleteAccount'))) {
      alert(t('admin:userDetail.accountDeleted'));
      window.location.href = '/admin/users';
    }
  };

  const getRoleBadgeVariant = (role: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (role) {
      case 'ADMIN': return 'error';
      case 'AUDITOR': return 'warning';
      case 'CONSULTANT': return 'info';
      case 'COMPANY': return 'success';
      default: return 'default';
    }
  };

  const getStatusBadgeVariant = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'INACTIVE': return 'default';
      case 'SUSPENDED': return 'error';
      default: return 'default';
    }
  };

  return (
    <PageLayout
      pageTitle={`${t('admin:userDetail.title')}: ${userData.name}`}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('admin:dashboard.title'), href: '/admin/dashboard' },
        { label: t('admin:users.title'), href: '/admin/users' },
        { label: userData.name },
      ]}
      actions={
        <div className="flex gap-2">
          {!editMode ? (
            <Button onClick={() => setEditMode(true)} icon={<Edit className="w-4 h-4" />}>
              {t('admin:userDetail.editProfile')}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setEditMode(false)}>
                {t('admin:actions.cancel')}
              </Button>
              <Button onClick={handleSave} icon={<Save className="w-4 h-4" />}>
                {t('admin:userDetail.saveChanges')}
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* User Profile */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('admin:userDetail.profile')}</h3>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin:users.name')}
                  </label>
                  <Input
                    name="name"
                    value={userData.name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setUserData({ ...userData, name: e.target.value })
                    }
                    disabled={!editMode}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin:users.email')}
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={userData.email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setUserData({ ...userData, email: e.target.value })
                    }
                    disabled={!editMode}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin:userDetail.phone')}
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={userData.phone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setUserData({ ...userData, phone: e.target.value })
                    }
                    disabled={!editMode}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin:users.organization')}
                  </label>
                  <Input
                    name="organization"
                    value={userData.organization}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setUserData({ ...userData, organization: e.target.value })
                    }
                    disabled={!editMode}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin:userDetail.roleSelector')}
                  </label>
                  <select
                    value={userData.role}
                    onChange={(e) => setUserData({ ...userData, role: e.target.value })}
                    disabled={!editMode}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    <option value="COMPANY">{t('admin:roles.company')}</option>
                    <option value="CONSULTANT">{t('admin:roles.consultant')}</option>
                    <option value="AUDITOR">{t('admin:roles.auditor')}</option>
                    <option value="ADMIN">{t('admin:roles.admin')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin:userDetail.statusToggle')}
                  </label>
                  <select
                    value={userData.status}
                    onChange={(e) => setUserData({ ...userData, status: e.target.value })}
                    disabled={!editMode}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    <option value="ACTIVE">{t('admin:userDetail.active')}</option>
                    <option value="INACTIVE">{t('admin:userDetail.inactive')}</option>
                    <option value="SUSPENDED">{t('admin:userDetail.suspended')}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <p className="text-sm font-medium text-gray-500">{t('admin:users.registrationDate')}</p>
                  <p className="mt-1">{formatDate(userData.registrationDate)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">{t('admin:users.lastLogin')}</p>
                  <p className="mt-1">{formatDate(userData.lastLogin)}</p>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Activity Log */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('admin:userDetail.activityLog')}</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-3">
                {activityLog.map((activity, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-gray-800">{activity.action}</p>
                        <p className="text-xs text-gray-400">{activity.timestamp}</p>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{activity.details}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          {/* Associated Records */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('admin:userDetail.associatedRecords')}</h3>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600">{t('admin:userDetail.applications')}</p>
                  <p className="text-2xl font-bold text-blue-600">{associatedRecords.applications}</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600">Documents</p>
                  <p className="text-2xl font-bold text-green-600">{associatedRecords.documents}</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-gray-600">Payments</p>
                  <p className="text-2xl font-bold text-purple-600">{associatedRecords.payments}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Quick Info</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t('admin:users.role')}</p>
                  <Badge variant={getRoleBadgeVariant(userData.role)}>
                    {t(`admin:roles.${userData.role.toLowerCase()}`)}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t('admin:users.status')}</p>
                  <Badge variant={getStatusBadgeVariant(userData.status)}>
                    {t(`admin:userDetail.${userData.status.toLowerCase()}`)}
                  </Badge>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('admin:users.actions')}</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-2">
                <Button variant="outline" fullWidth onClick={handleResetPassword} icon={<Key className="w-4 h-4" />}>
                  {t('admin:userDetail.resetPassword')}
                </Button>
                <Button variant="outline" fullWidth onClick={handleDeleteAccount} icon={<Trash2 className="w-4 h-4" />}>
                  {t('admin:userDetail.deleteAccount')}
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Permissions (Mock) */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('admin:userDetail.permissions')}</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked disabled={!editMode} className="w-4 h-4" />
                  <span className="text-sm">View Applications</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked disabled={!editMode} className="w-4 h-4" />
                  <span className="text-sm">Create Applications</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked disabled={!editMode} className="w-4 h-4" />
                  <span className="text-sm">Upload Documents</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" disabled={!editMode} className="w-4 h-4" />
                  <span className="text-sm">Delete Applications</span>
                </label>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

